import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  CreditCardIcon,
  TagIcon,
  LockClosedIcon,
  MapIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { api } from '../../services/api';
import type { PaymentMethod } from '../../types/subscription';
import { isHostedCheckoutEnabled } from '../../lib/featureFlags';
import { useAuth } from '../../providers/AuthProvider';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

function formatPrice(cents: number): string {
  return (cents / 100).toLocaleString('en-NZ', {
    style: 'currency',
    currency: 'NZD',
    minimumFractionDigits: 0,
  });
}

function formatTokens(tokens: number): string {
  if (tokens >= 1000000) return `${(tokens / 1000000).toFixed(1)}M`;
  if (tokens >= 1000) return `${(tokens / 1000).toFixed(0)}K`;
  return tokens.toString();
}

// -----------------------------------------------------------------------
// Inline "Add new card" form (same SetupIntent flow as AddPaymentModal)
// -----------------------------------------------------------------------
function InlinePaymentForm({
  onSuccess,
  onCancel,
}: {
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsSubmitting(true);
    setError(null);

    const { error: submitError } = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/dashboard/checkout`,
      },
      redirect: 'if_required',
    });

    if (submitError) {
      setError(submitError.message ?? 'Something went wrong.');
      setIsSubmitting(false);
      return;
    }

    onSuccess();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement options={{ layout: 'tabs' }} />

      {error && (
        <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3">
          {error}
        </div>
      )}

      <div className="flex items-center gap-2 text-sm text-slate-400 justify-center">
        <LockClosedIcon className="w-3.5 h-3.5" />
        Payments are secure and encrypted via Stripe
      </div>

      <div className="flex gap-3 justify-end">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-cyan-600 text-white rounded-lg font-medium hover:bg-cyan-500 transition-colors disabled:opacity-50 flex items-center gap-2"
          disabled={isSubmitting || !stripe || !elements}
        >
          {isSubmitting ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Saving...
            </>
          ) : (
            'Save Card'
          )}
        </button>
      </div>
    </form>
  );
}

// -----------------------------------------------------------------------
// Stripe Elements wrapper for the inline form
// -----------------------------------------------------------------------
function AddCardInline({
  onSuccess,
  onCancel,
}: {
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .createSetupIntent()
      .then((data) => setClientSecret(data.clientSecret))
      .catch((err) =>
        setError(err.response?.data?.error ?? 'Failed to initialise payment form.')
      )
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="w-6 h-6 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3">
        {error}
      </div>
    );
  }

  if (!clientSecret) return null;

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance: {
          theme: 'night',
          variables: {
            colorPrimary: '#06b6d4',
            colorBackground: '#0f172a',
            colorText: '#e2e8f0',
            colorDanger: '#ef4444',
            fontFamily: 'Inter, system-ui, sans-serif',
            borderRadius: '8px',
            spacingUnit: '4px',
          },
          rules: {
            '.Input': {
              border: '1px solid #334155',
              backgroundColor: '#1e293b',
            },
            '.Input:focus': {
              border: '1px solid #06b6d4',
              boxShadow: '0 0 0 1px #06b6d4',
            },
            '.Label': { color: '#94a3b8' },
          },
        },
      }}
    >
      <InlinePaymentForm onSuccess={onSuccess} onCancel={onCancel} />
    </Elements>
  );
}

// -----------------------------------------------------------------------
// Promo validation result type
// -----------------------------------------------------------------------
interface PromoResult {
  valid: boolean;
  promo_id?: string;
  promo_name?: string;
  amount_off?: number | null;
  percent_off?: number | null;
  currency?: string | null;
  duration?: string;
  duration_in_months?: number | null;
  reason?: string;
}

// -----------------------------------------------------------------------
// Hosted checkout view (simplified: plan summary + promo + redirect)
// -----------------------------------------------------------------------
function HostedCheckoutView({
  selectedPkg,
  promoParam,
  onBack,
}: {
  selectedPkg: { id: string; name: string; monthly_price_cents: number; included_tokens: number; features: string[] };
  promoParam: string | null;
  onBack: string;
}) {
  const { user } = useAuth();
  const emailVerified = user?.email_verified ?? false;

  const [promoInput, setPromoInput] = useState(promoParam ?? '');
  const [promoResult, setPromoResult] = useState<PromoResult | null>(null);
  const [promoValidating, setPromoValidating] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const validatePromo = useCallback(
    async (code: string) => {
      if (!code.trim()) return;
      setPromoValidating(true);
      setPromoResult(null);
      try {
        const result = await api.validatePromoCode(code.trim(), selectedPkg.id);
        setPromoResult(result);
      } catch {
        setPromoResult({ valid: false, reason: 'Failed to validate promo code' });
      } finally {
        setPromoValidating(false);
      }
    },
    [selectedPkg.id]
  );

  // Auto-validate promo from URL
  useEffect(() => {
    if (promoParam && !promoResult) {
      validatePromo(promoParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promoParam, selectedPkg.id]);

  // Debounced auto-validate as the user types
  useEffect(() => {
    if (!promoInput.trim()) return;
    const timer = setTimeout(() => {
      validatePromo(promoInput);
    }, 400);
    return () => clearTimeout(timer);
  }, [promoInput, validatePromo]);

  const quarterlyPrice = selectedPkg.monthly_price_cents * 3;
  const discountAmount =
    promoResult?.valid
      ? promoResult.amount_off
        ? Math.min(promoResult.amount_off, quarterlyPrice)
        : promoResult.percent_off
          ? Math.round((quarterlyPrice * promoResult.percent_off) / 100)
          : 0
      : 0;
  const totalDue = Math.max(0, quarterlyPrice - discountAmount);

  const handleHostedCheckout = async () => {
    setLoading(true);
    setCheckoutError(null);
    try {
      const promoCode = promoResult?.valid ? promoInput.trim() : undefined;
      const res = await api.createCheckoutSession(selectedPkg.id, undefined as any, promoCode);
      if (res?.url) {
        window.location.href = res.url;
      } else {
        setCheckoutError('No checkout URL received. Please try again.');
      }
    } catch (err: any) {
      setCheckoutError(err?.response?.data?.error || err?.message || 'Failed to start checkout');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-4 pb-8">
      {/* Header */}
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-xl font-bold text-white">Checkout</h1>
          <p className="text-sm text-slate-400">
            Review your order and complete your subscription
          </p>
        </div>
        <Link
          to={onBack}
          className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors"
        >
          Change plan
        </Link>
      </div>

      {/* Checkout Error */}
      {checkoutError && (
        <div className="p-3 bg-red-500/10 border border-red-500/50 rounded-lg flex items-start gap-3 text-sm">
          <ExclamationTriangleIcon className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-red-400 font-medium">Checkout failed</p>
            <p className="text-red-400/80">{checkoutError}</p>
          </div>
          <button onClick={() => setCheckoutError(null)} className="text-red-400 hover:text-red-300">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Selected Plan */}
      <section className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <MapIcon className="w-5 h-5 text-cyan-400 flex-shrink-0" />
            <div className="min-w-0">
              <h3 className="font-semibold text-white">{selectedPkg.name}</h3>
              <p className="text-sm text-slate-400 truncate">
                {formatTokens(selectedPkg.included_tokens * 3)} tokens/quarter
                {selectedPkg.features.length > 0 && ` · ${selectedPkg.features.slice(0, 2).join(' · ')}`}
              </p>
            </div>
          </div>
          <div className="text-right flex-shrink-0 pl-4">
            <span className="text-lg font-bold text-white">{formatPrice(quarterlyPrice)}</span>
            <span className="text-sm text-slate-400">/quarter</span>
          </div>
        </div>
      </section>

      {/* Promo Code */}
      <section className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
        <h2 className="text-sm font-semibold text-white flex items-center gap-2 mb-3">
          <TagIcon className="w-4 h-4 text-cyan-400" />
          Promo Code
        </h2>

        <div className="flex gap-2">
          <input
            type="text"
            value={promoInput}
            onChange={(e) => {
              setPromoInput(e.target.value.toUpperCase());
              if (promoResult) setPromoResult(null);
            }}
            onBlur={() => {
              if (promoInput.trim()) validatePromo(promoInput);
            }}
            placeholder="Enter promo code"
            className="flex-1 px-3 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:border-cyan-500 focus:outline-none text-sm uppercase tracking-wider"
          />
          <button
            onClick={() => validatePromo(promoInput)}
            disabled={!promoInput.trim() || promoValidating}
            className="px-4 py-2 bg-slate-700 text-white rounded-lg font-medium hover:bg-slate-600 transition-colors disabled:opacity-50 text-sm"
          >
            {promoValidating ? 'Checking...' : 'Apply'}
          </button>
        </div>

        {promoResult && (
          <div
            className={`mt-2 px-3 py-2 rounded-lg border text-sm ${
              promoResult.valid
                ? 'bg-green-500/10 border-green-500/30 text-green-400'
                : 'bg-red-500/10 border-red-500/30 text-red-400'
            }`}
          >
            {promoResult.valid ? (
              <div className="flex items-center gap-2">
                <CheckCircleIcon className="w-4 h-4 flex-shrink-0" />
                <span className="font-medium">{promoResult.promo_name}</span>
                <span className="text-green-400/80">
                  {discountAmount > 0
                    ? `−${formatPrice(discountAmount)}`
                    : promoResult.percent_off
                      ? `−${promoResult.percent_off}%`
                      : ''}
                  {promoResult.duration === 'once' && ' (first quarter only)'}
                  {promoResult.duration === 'repeating' &&
                    promoResult.duration_in_months &&
                    ` (${promoResult.duration_in_months} months)`}
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0" />
                <span>{promoResult.reason}</span>
              </div>
            )}
          </div>
        )}

        <p className="text-sm text-slate-500 mt-2">
          You can also enter a promo code on the Stripe checkout page.
        </p>
      </section>

      {/* Order Summary + CTA */}
      <section className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
        <h2 className="text-sm font-semibold text-white mb-3">Order Summary</h2>

        <div className="space-y-2 text-sm">
          <div className="flex justify-between text-slate-300">
            <span>{selectedPkg.name} — quarterly</span>
            <span>{formatPrice(quarterlyPrice)}</span>
          </div>

          {discountAmount > 0 && (
            <div className="flex justify-between text-green-400">
              <span>
                Promo: {promoResult?.promo_name}
                {promoResult?.duration === 'once' && ' (first quarter)'}
              </span>
              <span>−{formatPrice(discountAmount)}</span>
            </div>
          )}

          <div className="border-t border-slate-700 pt-2 flex justify-between text-white font-semibold">
            <span>Due today</span>
            <span className="text-cyan-400">{formatPrice(totalDue)}</span>
          </div>

          {discountAmount > 0 && promoResult?.duration === 'once' && (
            <p className="text-sm text-slate-500">
              Then {formatPrice(quarterlyPrice)}/quarter after the first period.
            </p>
          )}
        </div>

        {!emailVerified ? (
          <>
            <Link
              to="/dashboard/profile"
              className="w-full mt-4 px-6 py-3.5 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-xl font-semibold hover:from-amber-400 hover:to-orange-500 transition-colors flex items-center justify-center gap-2"
            >
              <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0" />
              Verify your email to continue
            </Link>

            <p className="text-sm text-amber-400/80 text-center mt-2">
              Please verify your email from your Profile before checking out.
            </p>
          </>
        ) : (
          <>
            <button
              onClick={handleHostedCheckout}
              disabled={loading}
              className="w-full mt-4 px-6 py-3.5 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl font-semibold hover:from-cyan-400 hover:to-blue-500 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Redirecting to Stripe...
                </>
              ) : (
                <>
                  <LockClosedIcon className="w-5 h-5" />
                  Continue to Checkout — {formatPrice(totalDue)}
                </>
              )}
            </button>

            <p className="text-sm text-slate-500 text-center mt-2">
              You'll be redirected to Stripe to securely complete your payment.
            </p>
          </>
        )}
      </section>
    </div>
  );
}

// -----------------------------------------------------------------------
// Main CheckoutPage
// -----------------------------------------------------------------------
export function CheckoutPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const packageSlug = searchParams.get('package');
  const promoParam = searchParams.get('promo');

  // ---- State ----
  const [promoInput, setPromoInput] = useState(promoParam ?? '');
  const [promoResult, setPromoResult] = useState<PromoResult | null>(null);
  const [promoValidating, setPromoValidating] = useState(false);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string | null>(null);
  const [showAddCard, setShowAddCard] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  // ---- Queries ----
  const { data: packages, isLoading: packagesLoading } = useQuery({
    queryKey: ['packages'],
    queryFn: () => api.getPackages(),
  });

  const { data: subData } = useQuery({
    queryKey: ['subscription'],
    queryFn: () => api.getCurrentSubscription(),
  });

  const {
    data: paymentMethods,
    isLoading: methodsLoading,
    refetch: refetchPaymentMethods,
  } = useQuery({
    queryKey: ['paymentMethods'],
    queryFn: () => api.getPaymentMethods(),
    retry: false,
  });

  const normalizeSlug = (s: string) => s.toLowerCase().replace(/[\s_-]+/g, '-');
  const selectedPkg = packageSlug
    ? packages?.find((p) => normalizeSlug(p.slug) === normalizeSlug(packageSlug)) ?? null
    : null;
  const existingSubscription = subData?.subscription;
  const isChangePlan = !!existingSubscription && !!existingSubscription.stripe_subscription_id;

  // Auto-select default payment method
  useEffect(() => {
    if (!paymentMethods || selectedPaymentMethodId) return;
    const defaultPm = paymentMethods.find((pm) => pm.is_default);
    if (defaultPm) setSelectedPaymentMethodId(defaultPm.id);
    else if (paymentMethods.length > 0) setSelectedPaymentMethodId(paymentMethods[0].id);
  }, [paymentMethods, selectedPaymentMethodId]);

  // Auto-validate promo from URL once the selected package is known
  useEffect(() => {
    if (promoParam && selectedPkg && !promoResult) {
      validatePromo(promoParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promoParam, selectedPkg?.id]);

  // Debounced auto-validate as the user types
  useEffect(() => {
    if (!promoInput.trim() || !selectedPkg) return;
    const timer = setTimeout(() => {
      validatePromo(promoInput);
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promoInput, selectedPkg?.id]);

  // ---- Promo validation ----
  const validatePromo = useCallback(
    async (code: string) => {
      if (!code.trim()) return;
      setPromoValidating(true);
      setPromoResult(null);
      try {
        const result = await api.validatePromoCode(code.trim(), selectedPkg?.id);
        setPromoResult(result);
      } catch {
        setPromoResult({ valid: false, reason: 'Failed to validate promo code' });
      } finally {
        setPromoValidating(false);
      }
    },
    [selectedPkg?.id]
  );

  // ---- Checkout mutation ----
  const checkoutMutation = useMutation({
    mutationFn: async () => {
      if (!selectedPkg) throw new Error('No package selected');

      if (isChangePlan) {
        await api.changeSubscription(selectedPkg.id);
        return { changed: true };
      }

      if (!selectedPaymentMethodId) throw new Error('No payment method selected');

      const promoCode = promoResult?.valid ? promoInput.trim() : undefined;
      return api.createCheckoutSession(selectedPkg.id, selectedPaymentMethodId, promoCode);
    },
    onSuccess: (data) => {
      if ('url' in data && data.url) {
        window.location.href = data.url;
      } else {
        queryClient.invalidateQueries({ queryKey: ['subscription'] });
        queryClient.invalidateQueries({ queryKey: ['usage'] });
        navigate('/dashboard/subscription?success=true');
      }
    },
    onError: (err: any) => {
      setCheckoutError(err?.response?.data?.error || err?.message || 'Checkout failed');
    },
  });

  // ---- Price calculation ----
  const quarterlyPrice = selectedPkg ? selectedPkg.monthly_price_cents * 3 : 0;
  const discountAmount =
    promoResult?.valid && selectedPkg
      ? promoResult.amount_off
        ? Math.min(promoResult.amount_off, quarterlyPrice)
        : promoResult.percent_off
          ? Math.round((quarterlyPrice * promoResult.percent_off) / 100)
          : 0
      : 0;
  const totalDue = Math.max(0, quarterlyPrice - discountAmount);

  // ---- Card added callback ----
  const handleCardAdded = async () => {
    setShowAddCard(false);
    const refreshed = await refetchPaymentMethods();
    const methods = refreshed.data;
    const newest = methods?.find((m) => m.is_default) ?? methods?.[methods.length - 1];
    if (newest) setSelectedPaymentMethodId(newest.id);
  };

  // ---- Loading state ----
  if (packagesLoading || methodsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // ---- No package selected ----
  if (!selectedPkg) {
    return (
      <div className="max-w-xl mx-auto text-center py-16 space-y-4">
        <h1 className="text-2xl font-bold text-white">No plan selected</h1>
        <p className="text-slate-400">
          Choose a plan to get started.
        </p>
        <Link
          to="/pricing"
          className="inline-block px-6 py-3 bg-cyan-600 text-white rounded-xl font-medium hover:bg-cyan-500 transition-colors"
        >
          View Plans
        </Link>
      </div>
    );
  }

  // ---- Hosted checkout: redirect to Stripe (new subs only) ----
  if (isHostedCheckoutEnabled() && !isChangePlan) {
    return (
      <HostedCheckoutView
        selectedPkg={selectedPkg}
        promoParam={promoParam}
        onBack={`/pricing${promoInput ? `?promo=${encodeURIComponent(promoInput)}` : ''}`}
      />
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-4 pb-8">
      {/* Header */}
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-xl font-bold text-white">
            {isChangePlan ? 'Change Plan' : 'Checkout'}
          </h1>
          <p className="text-sm text-slate-400">
            {isChangePlan
              ? `Switching from ${existingSubscription?.package?.name ?? 'current plan'} to ${selectedPkg.name}`
              : 'Review your order and complete your subscription'}
          </p>
        </div>
        <Link
          to={`/pricing${promoInput ? `?promo=${encodeURIComponent(promoInput)}` : ''}`}
          className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors"
        >
          Change plan
        </Link>
      </div>

      {/* Checkout Error */}
      {checkoutError && (
        <div className="p-3 bg-red-500/10 border border-red-500/50 rounded-lg flex items-start gap-3 text-sm">
          <ExclamationTriangleIcon className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-red-400 font-medium">Checkout failed</p>
            <p className="text-red-400/80">{checkoutError}</p>
          </div>
          <button onClick={() => setCheckoutError(null)} className="text-red-400 hover:text-red-300">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* ---- Selected Plan (compact) ---- */}
      <section className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <MapIcon className="w-5 h-5 text-cyan-400 flex-shrink-0" />
            <div className="min-w-0">
              <h3 className="font-semibold text-white">{selectedPkg.name}</h3>
              <p className="text-sm text-slate-400 truncate">
                {formatTokens(selectedPkg.included_tokens * 3)} tokens/quarter
                {selectedPkg.features.length > 0 && ` · ${selectedPkg.features.slice(0, 2).join(' · ')}`}
              </p>
            </div>
          </div>
          <div className="text-right flex-shrink-0 pl-4">
            <span className="text-lg font-bold text-white">{formatPrice(quarterlyPrice)}</span>
            <span className="text-sm text-slate-400">/quarter</span>
          </div>
        </div>
      </section>

      {/* ---- Promo Code (only for new subscriptions) ---- */}
      {!isChangePlan && (
        <section className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
          <h2 className="text-sm font-semibold text-white flex items-center gap-2 mb-3">
            <TagIcon className="w-4 h-4 text-cyan-400" />
            Promo Code
          </h2>

          <div className="flex gap-2">
            <input
              type="text"
              value={promoInput}
              onChange={(e) => {
                setPromoInput(e.target.value.toUpperCase());
                if (promoResult) setPromoResult(null);
              }}
              onBlur={() => {
                if (promoInput.trim()) validatePromo(promoInput);
              }}
              placeholder="Enter promo code"
              className="flex-1 px-3 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:border-cyan-500 focus:outline-none text-sm uppercase tracking-wider"
            />
            <button
              onClick={() => validatePromo(promoInput)}
              disabled={!promoInput.trim() || promoValidating}
              className="px-4 py-2 bg-slate-700 text-white rounded-lg font-medium hover:bg-slate-600 transition-colors disabled:opacity-50 text-sm"
            >
              {promoValidating ? 'Checking...' : 'Apply'}
            </button>
          </div>

          {promoResult && (
            <div
              className={`mt-2 px-3 py-2 rounded-lg border text-sm ${
                promoResult.valid
                  ? 'bg-green-500/10 border-green-500/30 text-green-400'
                  : 'bg-red-500/10 border-red-500/30 text-red-400'
              }`}
            >
              {promoResult.valid ? (
                <div className="flex items-center gap-2">
                  <CheckCircleIcon className="w-4 h-4 flex-shrink-0" />
                  <span className="font-medium">{promoResult.promo_name}</span>
                  <span className="text-green-400/80">
                    {discountAmount > 0
                      ? `−${formatPrice(discountAmount)}`
                      : promoResult.percent_off
                        ? `−${promoResult.percent_off}%`
                        : ''}
                    {promoResult.duration === 'once' && ' (first quarter only)'}
                    {promoResult.duration === 'repeating' &&
                      promoResult.duration_in_months &&
                      ` (${promoResult.duration_in_months} months)`}
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0" />
                  <span>{promoResult.reason}</span>
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {/* ---- Payment Method (skip for plan changes) ---- */}
      {!isChangePlan && (
        <section className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
          <h2 className="text-sm font-semibold text-white flex items-center gap-2 mb-3">
            <CreditCardIcon className="w-4 h-4 text-cyan-400" />
            Payment Method
          </h2>

          {paymentMethods && paymentMethods.length > 0 && (
            <div className="space-y-1.5 mb-3">
              {paymentMethods.map((pm: PaymentMethod) => (
                <label
                  key={pm.id}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors ${
                    selectedPaymentMethodId === pm.id
                      ? 'border-cyan-500 bg-cyan-500/10'
                      : 'border-slate-600 hover:border-slate-500'
                  }`}
                >
                  <input
                    type="radio"
                    name="payment-method"
                    checked={selectedPaymentMethodId === pm.id}
                    onChange={() => setSelectedPaymentMethodId(pm.id)}
                    className="sr-only"
                  />
                  <div
                    className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                      selectedPaymentMethodId === pm.id ? 'border-cyan-500' : 'border-slate-500'
                    }`}
                  >
                    {selectedPaymentMethodId === pm.id && (
                      <div className="w-1.5 h-1.5 rounded-full bg-cyan-500" />
                    )}
                  </div>
                  {pm.type === 'link' ? (
                    <LinkIcon className="w-4 h-4 text-sky-400 flex-shrink-0" />
                  ) : (
                    <CreditCardIcon className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  )}
                  <span className="text-white text-sm font-medium capitalize">
                    {pm.type === 'link' ? 'Link' : pm.brand ?? 'Card'}
                  </span>
                  {pm.type === 'card' && pm.last_four ? (
                    <span className="text-slate-400 text-sm">···· {pm.last_four}</span>
                  ) : pm.type === 'link' ? (
                    <span className="text-slate-400 text-sm truncate min-w-0">
                      {pm.email ?? 'Saved with Link'}
                    </span>
                  ) : null}
                  {pm.type === 'card' && pm.exp_year > 0 ? (
                    <span className="text-sm text-slate-500 ml-auto flex-shrink-0">
                      {String(pm.exp_month).padStart(2, '0')}/{pm.exp_year}
                    </span>
                  ) : pm.type === 'link' ? (
                    <span className="text-sm text-slate-500 ml-auto flex-shrink-0">Wallet</span>
                  ) : null}
                  {pm.is_default && (
                    <span className="text-sm bg-cyan-500/20 text-cyan-400 px-1.5 py-0.5 rounded-full">
                      Default
                    </span>
                  )}
                </label>
              ))}
            </div>
          )}

          {showAddCard ? (
            <div className="p-3 bg-slate-900/50 rounded-lg border border-slate-600">
              <AddCardInline
                onSuccess={handleCardAdded}
                onCancel={() => setShowAddCard(false)}
              />
            </div>
          ) : (
            <button
              onClick={() => setShowAddCard(true)}
              className="flex items-center gap-2 text-sm text-cyan-400 hover:text-cyan-300 transition-colors font-medium"
            >
              <span className="w-4 h-4 rounded-full border border-cyan-400 flex items-center justify-center text-[13px]">
                +
              </span>
              Add a new card
            </button>
          )}

          {!paymentMethods?.length && !showAddCard && (
            <p className="text-sm text-slate-400 mt-2">
              You need to add a payment method before you can subscribe.
            </p>
          )}
        </section>
      )}

      {/* ---- Order Summary + CTA ---- */}
      <section className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
        <h2 className="text-sm font-semibold text-white mb-3">Order Summary</h2>

        <div className="space-y-2 text-sm">
          <div className="flex justify-between text-slate-300">
            <span>{selectedPkg.name} — quarterly</span>
            <span>{formatPrice(quarterlyPrice)}</span>
          </div>

          {discountAmount > 0 && (
            <div className="flex justify-between text-green-400">
              <span>
                Promo: {promoResult?.promo_name}
                {promoResult?.duration === 'once' && ' (first quarter)'}
              </span>
              <span>−{formatPrice(discountAmount)}</span>
            </div>
          )}

          <div className="border-t border-slate-700 pt-2 flex justify-between text-white font-semibold">
            <span>{isChangePlan ? 'New plan price' : 'Due today'}</span>
            <span className="text-cyan-400">
              {isChangePlan
                ? `${formatPrice(quarterlyPrice)}/quarter`
                : discountAmount > 0
                  ? formatPrice(totalDue)
                  : formatPrice(totalDue)}
            </span>
          </div>

          {discountAmount > 0 && promoResult?.duration === 'once' && (
            <p className="text-sm text-slate-500">
              Then {formatPrice(quarterlyPrice)}/quarter after the first period.
            </p>
          )}

          {isChangePlan && (
            <p className="text-sm text-slate-500">
              Stripe will handle proration automatically. You may see a charge or credit for the
              remainder of your current billing period.
            </p>
          )}
        </div>

        <button
          onClick={() => checkoutMutation.mutate()}
          disabled={
            checkoutMutation.isPending ||
            (!isChangePlan && !selectedPaymentMethodId)
          }
          className="w-full mt-4 px-6 py-3.5 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl font-semibold hover:from-cyan-400 hover:to-blue-500 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {checkoutMutation.isPending ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <LockClosedIcon className="w-5 h-5" />
              {isChangePlan
                ? `Change to ${selectedPkg.name}`
                : `Subscribe — ${formatPrice(totalDue)}`}
            </>
          )}
        </button>
      </section>
    </div>
  );
}

export default CheckoutPage;
